import React from "react";
import { Link, Link as RouterLink } from "react-router-dom";
import { makeStyles, Typography } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import { Edit, Delete } from "@material-ui/icons";
import { useGlobalFilter, usePagination, useTable } from "react-table";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { ImportButton } from "../../FilmListingPage";
import PaginationBar from "../../../common/PaginationBar";

const useStyles = makeStyles((theme) => ({
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  text: {
    textDecoration: "none",
    color: "#306F9D",
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
}));

const FilmListPage = (props) => {
  const { match, disabled } = props;
  const classes = useStyles();
  const [data, setData] = React.useState([]);
  const [keyword, setKeyword] = React.useState("");
  const [queryString, setQueryString] = React.useState("");
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalElements, setTotalElements] = React.useState(0);
  const { setLoading } = useLoading();

  const formatDatetime = (dt) => {
    return dayjs(dt).format("DD MMM YYYY, hh:mma");
  };

  const CustomLink = React.useMemo(
    () =>
        React.forwardRef((linkProps, ref) => <Link
            ref={ref} {...linkProps} />),
    []
  );

  const handleFileUpload = async (event, reset) => {
    event.preventDefault();
    const fd = new FormData();
    fd.append("file", event.target.files[0]);
    try {
      const result = await apiHelper.hkiff.importAcaFilm(fd);
      if (0 === result?.data?.code) {
        toast.dark("Successfully imported.");
        getFilmList();
      }
    } catch (err) {
      console.log(err);
    } finally {
      reset();
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "id",
        accessor: (row) => {
          return row.id === null ? "-" : row.id;
        },
      },
      {
        Header: "Name",
        accessor: (row) => {
          return row.name === null ? "-" : row.name;
        },
      },
      {
        Header: "Director",
        accessor: (row) => {
          return row.director === null ? "-" : row.director;
        },
      },
      {
        Header: "Vote Date",
        accessor: (row) => {
          return row.voteDate === null ? "-" : formatDatetime(row.voteDate);
        },
      },
      {
        Header: "Ticket for Sales",
        accessor: (row) => {
          return row.totalSeat === null ? "-" : row.totalSeat;
        },
      },
      {
        Header: "Ticket Sold",
        accessor: (row) => {
          return row.ticketSold === null ? "-" : row.ticketSold;
        },
      },
      {
        Header: "Total Vote",
        accessor: (row) => {
          return row.totalVotes === null ? "-" : row.totalVotes;
        },
      },
      {
        Header: "Score of Star",
        accessor: (row) => {
          return row.totalStarScore === null ? "-" : row.totalStarScore;
        },
      },
      {
        Header: "Final Score",
        accessor: (row) => {
          return row.score === null ? "-" : Number(row.score).toFixed(2);
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    setPageSize,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      manualPagination: true,
      autoResetPage: false,
      pageCount: totalPages,
    },
    useGlobalFilter,
    usePagination,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          id: "action",
          Header: () => <div>Action</div>,
          Cell: ({ row }) => (
              <div
                  style={{ display: "inline-flex" }}>
                  <IconButton
                      size="small"
                      component={CustomLink}
                      to={`/hkiff/film/${row.original.id}`}
                  >
                      <Edit fontSize="small" />
                  </IconButton>
              </div>
          ),
      },
      ]);
    }
  );

  const triggerSearch = React.useCallback(() => {
    setQueryString(keyword);
    gotoPage(0);
  }, [gotoPage, keyword]);

  const getFilmList = React.useCallback(async () => {
    try {
      setLoading(true);
      const results = await apiHelper.hkiff.getHkiffAcaFilmListing({
        keyword: queryString,
        page: pageIndex,
        size: pageSize,
      });
      console.log("RESULT:", results);
      setData(results.data.content);
      setTotalElements(results.data.totalElements);
      setTotalPages(Math.ceil(results.data.totalElements / pageSize));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, queryString, setLoading]);

  React.useEffect(() => {
    getFilmList();
  }, [getFilmList]);

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
        {"Film List"}
      </Typography>
      <Grid container justify="space-between" style={{ paddingBottom: "10px" }}>
        <Grid container item xs={6} md={2}></Grid>
        <Grid container item xs={6} md={10}>
          <Box
            width="100%"
            component="span"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box display="flex">
              <ImportButton
                onChangeHandler={handleFileUpload}
                disabled={disabled}
              />
            </Box>
            <Box display="flex">
              <TextField
                id="outlined-error"
                label="Search Ticket"
                variant="outlined"
                margin="dense"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                type="button"
                margin="dense"
                onClick={triggerSearch}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container xs={12}>
        <TableContainer
          component={Paper}
          style={{
            boxShadow: "none",
            border: "1px solid #cccccc",
          }}
        >
          <fieldset disabled={disabled} className="fieldset">
            <Table
              aria-label="simple table"
              component="ul"
              style={{ margin: "0" }}
              {...getTableProps()}
            >
              <colgroup>
                <col style={{ width: "1%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <TableHead component="div">
                {headerGroups.map((headerGroup) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <TableCell
                        {...column.getHeaderProps()}
                        className={`${column.className ?? ""}`}
                        style={{
                          paddingTop: 6,
                          paddingBottom: 6,
                          lineHeight: "1.1em",
                          height: "48px",
                        }}
                      >
                        {column.render("Header")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  prepareRow(row);
                  console.log("row", row);
                  return (
                    <TableRow {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <TableCell
                            {...cell.getCellProps()}
                            className={`${cell.column.className ?? ""}`}
                            style={{
                              paddingTop: 6,
                              paddingBottom: 6,
                              lineHeight: "1.1em",
                              height: "48px",
                            }}
                          >
                            {cell.render("Cell")}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </fieldset>
        </TableContainer>
      </Grid>
      <Grid
        container
        justify="flex-end"
        style={{
          paddingRight: "25px",
          marginTop: "15px",
        }}
      ></Grid>
      <Grid container spacing={12} justifyContent="space-between" alignItems="center">
        <Grid container item xs={12} justifyContent="flex-end">
          <PaginationBar
            pageSize={pageSize}
            setPageSize={setPageSize}
            gotoPage={gotoPage}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageIndex={pageIndex}
            totalCount={totalElements}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default FilmListPage;
